import {AnalyticsController} from '../../../../reporting/reporting-pages/admin-analytics/admin-analytics.controller';
import {AnalyticsRequestImpl} from '../../../../reporting/reporting-pages/admin-analytics/admin-analytics.request';
import {useCallback} from 'react';
import {toJS as proxyToJS} from 'mobx';
import dayjs from 'dayjs';
import {useStoreContext} from '@store/store-context';
import {useQuery} from 'react-query';
import {GoalsRequestImpl} from '@api/goals-api/goals-request';
import {GoalsController} from '@api/goals-api/goals-controller';
import {
  addFeedHistoryToResults,
  buildOverallGoalProgressHistory,
} from '../activity/insight/goal-progress-history';

interface Activity {
  endDate: string;
  prevValue?: number;
  percentageProgress?: number;
  performance?: 'on_track' | 'at_risk' | 'behind';
  isGoalEndDate?: boolean;
  isTodayDate?: boolean;
  [key: string]: any;
}

export const mergeKpiMetricValues = (reportGoal: any, goalDetail: any) => {
  if (!reportGoal?.isKpi || !goalDetail) return reportGoal;

  return {
    ...reportGoal,
    startValue: goalDetail.startValue ?? reportGoal.startValue,
    currentValue: goalDetail.currentValue ?? reportGoal.currentValue,
    targetValue: goalDetail.targetValue ?? reportGoal.targetValue,
    value: goalDetail.value ?? reportGoal.value,
  };
};

export function processActivityData(
  activities: Activity[],
  todayDate: string,
  goalEndDate?: string,
): Activity[] {
  const formattedGoalEndDate = dayjs(goalEndDate).format('MM/DD/YYYY');
  const formattedTodayDate = dayjs(todayDate).format();

  // Create a new array with all activities, goal end date, and today date
  const allDates = () => {
    const options = [
      ...activities,
      {endDate: formattedTodayDate, isTodayDate: true},
    ];

    if (goalEndDate) {
      options.push({endDate: formattedGoalEndDate, isGoalEndDate: true} as any);
    }

    return options;
  };

  // Sort the array by date
  const sortedDates = allDates().sort(
    (a, b) => dayjs(a.endDate).valueOf() - dayjs(b.endDate).valueOf(),
  );

  // Function to carry over values from the previous activity
  const carryOverValues = (index: number) => {
    if (index > 0) {
      const previousActivity: any = sortedDates[index - 1];

      return {
        prevValue: previousActivity.prevValue || previousActivity.currentValue,
        currentValue: previousActivity.currentValue,
        percentageProgress: previousActivity.percentageProgress,
      };
    }
    return {};
  };

  // Process goal end date and today date
  let lastRegularActivityIndex = -1;
  sortedDates.forEach((item, index) => {
    if (!item.isGoalEndDate && !item.isTodayDate) {
      lastRegularActivityIndex = index;
    }
    const isGoalInFuture =
      item.isGoalEndDate && dayjs(item.endDate).isAfter(todayDate);

    if (item.isTodayDate) {
      Object.assign(item, {});
    }

    if (item.isGoalEndDate && !isGoalInFuture) {
      Object.assign(item, carryOverValues(lastRegularActivityIndex + 1));
    } else {
      Object.assign(item, {});
    }
  });

  // Remove any duplicate dates, keeping the one with more information
  const uniqueDates = sortedDates.reduce((acc: Activity[], current: any) => {
    const existingIndex = acc.findIndex(
      (item) =>
        dayjs(item.endDate).format() === dayjs(current.endDate).format(),
    );
    if (existingIndex === -1) {
      acc.push(current);
    } else {
      // Merge properties, prioritizing non-undefined values
      acc[existingIndex] = Object.entries(current).reduce(
        (merged, [key, value]) => {
          if (value !== undefined) {
            merged[key] = value;
          }
          return merged;
        },
        acc[existingIndex],
      );
    }
    return acc;
  }, []);

  return uniqueDates;
}

export const useFetchGoalById = () => {
  const {
    groupStore: {groups},
  } = useStoreContext();

  const getGoalDetails = useCallback(
    async (id: string) => {
      const request = new AnalyticsRequestImpl();
      const controller = new AnalyticsController(request);

      const response: any = await controller.fetchReportGoalById(id);

      if (response) {
        const firstActivityPage = !response.isKpi
          ? await controller.fetchReportGoalActivitiesById(id, 100)
          : undefined;
        const activityFeed = firstActivityPage?.results || [];
        const totalActivities = Number(firstActivityPage?.totalResults) || 0;
        const completeActivityFeed =
          totalActivities > activityFeed.length
            ? (
                await controller.fetchReportGoalActivitiesById(
                  id,
                  totalActivities,
                )
              )?.results || activityFeed
            : activityFeed;
        const goalDetail = response.isKpi
          ? await new GoalsController(new GoalsRequestImpl()).fetchGoalById(
              id,
              true,
            )
          : undefined;
        const goalWithCurrentMetric = mergeKpiMetricValues(
          response,
          goalDetail,
        );

        const includeGroups = async (goalsResponse: any) => {
          if (goalsResponse.goalAlignment) {
            const alignment: any = await controller.fetchReportGoalById(
              goalsResponse.goalAlignment.id,
            );

            goalsResponse.goalAlignment =
              alignment || goalsResponse.goalAlignment;
          }
          if (goalsResponse.goalType === 'group') {
            const group = goalsResponse.group.map((grp: string) => {
              return proxyToJS(groups.find((_group) => _group.id === grp));
            });

            return {
              ...goalsResponse,
              group: group,
            };
          } else {
            return {
              ...goalsResponse,
            };
          }
        };

        const unfurledData = await includeGroups(goalWithCurrentMetric);

        if (unfurledData) {
          const res = [
            ...processActivityData(
              unfurledData?.activities,
              dayjs().format(),
              !unfurledData?.isKpi
                ? dayjs(unfurledData?.endDate).format()
                : undefined,
            ),
          ];

          const resultsWithFeedHistory = addFeedHistoryToResults(
            unfurledData.keyResults || [],
            completeActivityFeed,
          );
          const keyResultActivities = resultsWithFeedHistory.map((kr: any) => {
            return {
              ...kr,
              activities: [
                ...processActivityData(
                  kr.activities,
                  dayjs().format(),
                  dayjs(unfurledData?.endDate).format(),
                ),
              ],
            };
          });
          const resultDrivenHistory = buildOverallGoalProgressHistory(
            keyResultActivities,
          );

          return {
            ...unfurledData,
            activities:
              !unfurledData.isKpi && resultDrivenHistory.length
                ? resultDrivenHistory
                : res,
            keyResults: keyResultActivities,
          };
        }
      }
    },
    [groups],
  );

  return {getGoalDetails};
};
export const useInsightHook = (goalId?: string) => {
  const {getGoalDetails} = useFetchGoalById();

  const {data, isLoading, refetch} = useQuery(
    ['report-goal', goalId],
    () => getGoalDetails(goalId || ''),
    {
      enabled: !!goalId,
    },
  );

  return {
    getGoalDetails,
    goal: data,
    loading: isLoading,
    refetch,
  };
};
